// Readiness-aware advisory (W4.2). On the DAY of a planned, intense workout,
// if today's readiness is low, we surface a non-destructive nudge to ease off.
// We NEVER modify the plan — the coach leads (канон: тренер ведёт); this only advises.
// The push-time approach was dropped: a workout is scheduled for a future date,
// and readiness can only be computed for today — so the nudge lives on the day.
//
// ═══ ДВА СИГНАЛА, А НЕ ОДИН — 2026-08-04 ═══
//
// Совет молчал у живого атлета по трём причинам сразу, и каждая делала его
// бесполезным по отдельности:
//
//   1. Смотрел ТОЛЬКО готовность — суточный сигнал (сон, HRV). У Димы она была
//      62 при пороге 55, то есть «нормально»: выход из функции до всех прочих
//      проверок. При этом свежесть стояла на −17 — глубокая усталость по нашей
//      же шкале, накопленная за недели. Один сигнал говорил «выспался»,
//      другой — «ты в яме третью неделю», и решение принималось по первому.
//   2. «Интенсивность» определялась по is_key_workout / intensity_factor /
//      planned_load. У плана из intervals.icu все три пусты — замер на проде:
//      шесть назначенных подряд, ни одного заполненного поля. То есть даже при
//      низкой готовности день никогда не считался тяжёлым.
//   3. Поэтому «Сегодня: Run threshold 5x6» и «свежесть −17» спокойно стояли на
//      одном экране в трёх сантиметрах друг от друга.
//
// Теперь тяжесть дня берётся из структуры работы (максимальная зона — прямое
// указание её автора), а состояние — из ДВУХ сигналов: суточной готовности и
// накопленной усталости. Ступень усталости — из общей шкалы, той же, что красит
// плитку TSB и подписывает линзу.
import { freshnessState } from "./lib/freshnessScale";
import type { WorkoutDetail } from "./workoutDetailApi";

/**
 * `reason` объясняет, ПОЧЕМУ совет появился: одно и то же «сбавь» по разным
 * причинам требует разных слов. Экран без этого пришлось бы догадываться.
 */
export type ReadinessAdvice = {
  level: "reduce";
  reason: "low_readiness" | "deep_fatigue";
  readinessScore: number | null;
  tsb: number | null;
};

type AdviceWorkout = Pick<
  WorkoutDetail,
  "status" | "scheduled_date" | "is_key_workout" | "intensity_factor" | "planned_load"
> & {
  /** Максимальная зона из структуры (бэкенд, athlete_plan.max_zone). */
  max_zone?: number | null;
};

// Tunable thresholds (kept explicit so intent is legible, not magic).
const LOW_READINESS = 55;      // < this → "low" for a hard day
const HARD_IF = 0.85;          // intensity factor at/above → intense
const HARD_LOAD = 80;          // planned load at/above → intense
/**
 * Z4 — пороговая работа и выше. Ниже неё «тяжёлым днём» считать нечего: Z3 в
 * длинной работе это рабочий фон, ради которого разгрузку не двигают.
 */
const HARD_ZONE = 4;

/** Тяжёлый ли сегодняшний день — по любому из доступных признаков. */
export function isIntenseWorkout(workout: AdviceWorkout): boolean {
  return (
    workout.is_key_workout ||
    (workout.max_zone != null && workout.max_zone >= HARD_ZONE) ||
    (workout.intensity_factor != null && workout.intensity_factor >= HARD_IF) ||
    (workout.planned_load != null && workout.planned_load >= HARD_LOAD)
  );
}

export function readinessWorkoutAdvice(args: {
  readinessScore: number | null;
  /** Свежесть (TSB) на сегодня. null — кривой ещё нет, судим по готовности. */
  tsb?: number | null;
  todayIso: string;
  workout: AdviceWorkout | null;
}): ReadinessAdvice | null {
  const { readinessScore, tsb = null, todayIso, workout } = args;
  if (workout == null) return null;
  if (workout.status !== "planned") return null;          // only before it's done
  if (workout.scheduled_date !== todayIso) return null;   // a same-day nudge only
  if (!isIntenseWorkout(workout)) return null;            // easy day → nothing to ease

  // Накопленная усталость — самостоятельный повод, не поправка к готовности.
  // Выспаться в глубокой яме можно; выйти из неё за одну ночь нельзя.
  if (tsb != null && freshnessState(tsb) === "deep") {
    return { level: "reduce", reason: "deep_fatigue", readinessScore, tsb };
  }
  if (readinessScore != null && readinessScore < LOW_READINESS) {
    return { level: "reduce", reason: "low_readiness", readinessScore, tsb };
  }
  return null;
}
